import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, Tab, Col, Row, Nav, NavItem } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import { useTransactionDocumentStore } from '../../store/providers/TransactionDocument';
import { useTransactionDocumentLookupsStore } from '../../store/providers/TransactionDocumentLookups';
import { useTransactionDocumentSourceStore } from '../../store/providers/TransactionDocumentSource';
import Permission from '../../components/Permission/Permission';

import Spinner from '../../components/Spinner/Spinner';
import * as constants from '../../constants';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const InfoTab = React.lazy(() => import('../../components/Tabs/FranchiseDocumentSetupTab/InfoTab'));
const SourceTab = React.lazy(() => import('../../components/Tabs/FranchiseDocumentSetupTab/SourceTab'));
const PagesTab = React.lazy(() => import('../../components/Tabs/FranchiseDocumentSetupTab/PagesTab'));

const TransactionDocumentSetup = (props) => {
    const dispatch = useDispatch();

    const { selectRecord, data, newRecord } = useTransactionDocumentStore();
    const { selectRecord: getDocumentSource, data: documentSourceData } = useTransactionDocumentSourceStore();
    const { getLookupData, documentEditerTab: selectedDocumentSetupTab, setDocumentEditerTab: setSelectedDocumentSetupTab, setFranchiseTabKey: setSelectedFranchiseTab, franchiseInstance } = useTransactionDocumentLookupsStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [documentSource, setDocumentSource] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const pagePermission = constants.PERMISSION_FRANCHISES_REALESTATE_DOCUMENTSETUP_TRANSACTIONS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const tabSelectedHandler = (tabKey) => {
        if (tabKey !== constants.DOCUMENT_EDIT_SETUP.Mapping) {
            setSelectedDocumentSetupTab(tabKey);
        } else {
            const instanceId = (data && data.body && data.body.franchiseInstance)
                || (documentSource && documentSource.franchiseInstance);
            if (props.documentId && instanceId) {
                navigate(`/documentMapper/${props.documentId}/${instanceId}`);
            }
        }
    };

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) {
            return;
        }

        dispatch(breadcrumbAddRoute({ key: 'instances', text: 'Instances', path: '/instances' }));
        dispatch(breadcrumbAddRoute({
            key: 'edit-franchise',
            text: franchiseInstance,
            path: {
                event: () => {
                    dispatch(breadcrumbRemoveRoute(3));
                    setSelectedFranchiseTab(constants.DEFAULT_FRANCHISE_TAB);
                    navigate(`/instances/edit/${props.franchiseId}`);
                }
            }
        }));
        dispatch(breadcrumbAddRoute({
            key: 'franchise-document-setup',
            parent: 'Real Estate',
            text: 'Document Setup',
            path: {
                event: () => {
                    setSelectedFranchiseTab('document_setup');
                    navigate(`/instances/edit/${props.franchiseId}`);
                }
            }
        }));

        if (!_.isEmpty(props.documentId)) {
            selectRecord(
                {
                    documentId: props.documentId,
                    franchiseId: props.franchiseId
                }
            );
        } else {
            const selectedType = !_.isEmpty(props.categoryId) && parseInt(props.categoryId) === constants.DOCUMENT_CATEGORY_ENUM.Transaction
                ? constants.DOCUMENT_CATEGORY_ENUM.Transaction
                : constants.DOCUMENT_CATEGORY_ENUM.AgentOrProspect
            getLookupData(
                {
                    type: selectedType,
                    franchiseId: props.franchiseId
                }
            );
            newRecord();
            dispatch(breadcrumbAddRoute({
                key: 'franchise-document-transaction',
                text: selectedType === constants.DOCUMENT_CATEGORY_ENUM.Transaction ? 'Transactions' : 'Agents/ Prospects',
                path: {
                    event: () => {
                        setSelectedFranchiseTab('document_setup');
                        navigate(`/instances/edit/${props.franchiseId}`);
                    }
                }
            }));
            dispatch(breadcrumbAddRoute({ key: 'franchise-document-create', text: 'Create Document' }));
        }

        setIsMounted(true);
        setIsBusy(false);
    }, [isMounted, hasPermission, dispatch, props.franchiseId, props.documentId, selectRecord, getLookupData, newRecord, props.categoryId, franchiseInstance, setSelectedFranchiseTab]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (!isLoaded && isMounted && data.scope === 'select' && data.status === 'success') {
                if (data.body.document) {
                    getDocumentSource({
                        documentId: data.body.document.id,
                        type: data.body.document.documentCategoryTypeId,
                        franchiseId: data.body.document.franchiseId
                    });
                }
                dispatch(breadcrumbAddRoute({
                    key: 'franchise-document-transaction',
                    text: data.body.document.documentCategoryTypeId === constants.DOCUMENT_CATEGORY_ENUM.Transaction ? 'Transactions' : 'Agents/ Prospects',
                    path: {
                        event: () => {
                            setSelectedFranchiseTab('document_setup');
                            navigate(`/instances/edit/${props.franchiseId}`);
                        }
                    }
                }));
                setIsLoaded(true);
                dispatch(breadcrumbAddRoute({ key: 'franchise-document-create', text: 'Edit Document' }));
            }
        }
    }, [props, data, isLoaded, isMounted, getDocumentSource, dispatch, setSelectedFranchiseTab]);

    useEffect(() => {
        if (!_.isEmpty(documentSourceData) && documentSourceData.scope === 'select' && documentSourceData.status === 'success') {
            if (documentSourceData.body.document.id === (props.documentId && parseInt(props.documentId))) {
                setDocumentSource(documentSourceData.body);
            }
        }

        if (isLoaded && isMounted && !_.isEmpty(documentSourceData)
            && documentSourceData.status === 'success' && documentSourceData.scope === 'create') {
            setDocumentSource({
                document: documentSourceData.body,
                franchiseInstance: documentSourceData.body.franchise && documentSourceData.body.franchise.internalId,
                franchiseId: documentSourceData.body.franchiseId
            });
        }

        if (isLoaded && isMounted && !_.isEmpty(documentSourceData)
            && documentSourceData.status === 'success' && documentSourceData.scope === 'delete') {
            setDocumentSource();
        }
    }, [documentSourceData, isLoaded, isMounted, props.documentId]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute(4));
        };
    }, [dispatch]);

    const isDisabled = data && data.scope === 'new' && data.status === 'success';
    const isDisabledMapping = (documentSource && documentSource.document && !documentSource.document.fileTemplateId)
        || documentSource === undefined;

    return (
        <>
            <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
            {hasPermission && isMounted &&
                <Suspense fallback={<Spinner text="Loading Document Setup..." />}>
                    <SideMenu activeKey="franchises" />
                    <div className="content-body">
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader
                                title={`${props.documentId
                                    ? 'Edit'
                                    : 'Create'} Document`} />
                            <Panel>
                                <Panel.Body>
                                    <Tab.Container defaultActiveKey={constants.DOCUMENT_EDIT_SETUP.Info} activeKey={selectedDocumentSetupTab} onSelect={tabSelectedHandler} id="documentSetupTab">
                                        <Row className="clearfix">
                                            <Col sm={12}>
                                                <Nav bsStyle="pills">
                                                    <NavItem eventKey={constants.DOCUMENT_EDIT_SETUP.Info} disabled={isBusy}>Info</NavItem>
                                                    <NavItem eventKey={constants.DOCUMENT_EDIT_SETUP.Source} disabled={isDisabled}>Source</NavItem>
                                                    <NavItem eventKey={constants.DOCUMENT_EDIT_SETUP.Pages} disabled={isDisabled}>Pages</NavItem>
                                                    <NavItem eventKey={constants.DOCUMENT_EDIT_SETUP.Mapping} disabled={isDisabled || isDisabledMapping}>Mapping</NavItem>
                                                </Nav>
                                            </Col>
                                            <Col sm={12}>
                                                <Tab.Content animation>
                                                    <Tab.Pane eventKey={constants.DOCUMENT_EDIT_SETUP.Info}>
                                                        {
                                                            (!isBusy && selectedDocumentSetupTab === constants.DOCUMENT_EDIT_SETUP.Info) && <InfoTab canEdit={canEdit} compType="info" {...props} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey={constants.DOCUMENT_EDIT_SETUP.Source}>
                                                        {
                                                            (!isBusy && selectedDocumentSetupTab === constants.DOCUMENT_EDIT_SETUP.Source) && <SourceTab canEdit={canEdit} compType="source" {...props} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey={constants.DOCUMENT_EDIT_SETUP.Pages}>
                                                        {
                                                            (!isBusy && selectedDocumentSetupTab === constants.DOCUMENT_EDIT_SETUP.Pages) && <PagesTab canEdit={canEdit} compType="pages" {...props} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey={constants.DOCUMENT_EDIT_SETUP.Mapping}>
                                                        <div></div>
                                                    </Tab.Pane>
                                                </Tab.Content>
                                            </Col>
                                        </Row>
                                    </Tab.Container>
                                </Panel.Body>
                            </Panel>
                        </div>
                    </div>
                </Suspense>
            }
        </>
    );
}

export default withAuthentication(withLayout(TransactionDocumentSetup));